// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setDropShipModalIsOpen from "@guestsupply/App/Handlers/AddToListModal/SetDropShipModalIsOpen";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { hasDropShipmentItemInCart } from "@guestsupply/App/Utilities/Cart.helper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrderForApproval";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: ButtonPresentationProps;
    isChecked?: boolean;
    setShowTermsAndConditionsErrorMessage?: (value: boolean) => any;
}

const mapStateToProps = (state: CustomAppState) => {
    const { isPlacingOrder } = state.pages.checkoutReviewAndSubmit;
    return {
        isDisabled: isPlacingOrder,
        orderApprovalDetailsLink: getPageLinkByPageType(state, "OrderApprovalDetailsPage"),
        // xng-change
        cart: getCurrentCartState(state).value,
        enableDropShipModule: getSettingsCollection(state).customSettings?.enableDropShipModule,
    };
};

const mapDispatchToProps = {
    placeOrderForApproval,
    // xng-change
    setDropShipModalIsOpen,
};

export interface SubmitForApprovalButtonStyles {
    submitForApprovalButton?: ButtonPresentationProps;
}

export const submitForApprovalButtonStyles: SubmitForApprovalButtonStyles = {};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const CheckoutReviewAndSubmitSubmitForApprovalButton = ({
    isDisabled,
    placeOrderForApproval,
    orderApprovalDetailsLink,
    history,
    toaster,
    extendedStyles,
    // XNG-CHANGES
    isChecked,
    setShowTermsAndConditionsErrorMessage,
    cart,
    enableDropShipModule,
    setDropShipModalIsOpen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(submitForApprovalButtonStyles.submitForApprovalButton, extendedStyles));

    const handleClick = () => {
        // XNG-CHANGES
        if (!isChecked) {
            setShowTermsAndConditionsErrorMessage?.(true);
            return;
        }

        // <xng-change>
        const dropShipInfoMissing = !cart?.properties?.dropShipContactInfo;
        const cartHasDropShipItem = hasDropShipmentItemInCart(cart?.cartLines);

        if (enableDropShipModule && cartHasDropShipItem && dropShipInfoMissing) {
            setDropShipModalIsOpen({ modalIsOpen: true });
            return;
        }
        // </xng-change>

        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                if (orderApprovalDetailsLink) {
                    history.push(`${orderApprovalDetailsLink.url}?cartId=${cartId}`);
                } else {
                    toaster.addToast({
                        body: translate("Order approval details page doesn't exist."),
                        messageType: "danger",
                    });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };

    return (
        <Button
            type="submit"
            disabled={isDisabled}
            data-test-selector="checkoutReviewAndSubmit_submitForApproval"
            {...styles}
            onClick={handleClick}
        >
            {translate("Submit For Approval")}
        </Button>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withToaster(withHistory(CheckoutReviewAndSubmitSubmitForApprovalButton)));
